/**
 * La competicion mundial: puntuacion, divisiones y temporadas.
 *
 * Vive en shared porque el servidor la calcula y el juego la pinta, y las dos
 * cosas tienen que decir lo mismo. SOLO cuentan los partidos online que el
 * servidor ha arbitrado el mismo, y para la puntuacion solo los que tenian
 * personas en los dos equipos: contra bots no se sube.
 *
 * Puntuacion tipo Elo por equipos: tu equipo contra la media del rival. Se
 * empieza en 1000, se sube rapido en los primeros partidos (K alto) y despues
 * se asienta. Cada mes es una temporada nueva y la puntuacion se acerca a 1000
 * a la mitad: se conserva lo ganado, pero todos tienen algo que remontar.
 */
export const RATING_START = 1000;
/** Partidos contra personas antes de tener division. */
export const PLACEMENT_GAMES = 5;
/** Partidos con K alto (se coloca rapido). */
export const PROVISIONAL_GAMES = 10;
export const K_PROVISIONAL = 48;
export const K_SETTLED = 24;
/** Al empezar temporada: 1000 + (lo que tenias - 1000) * esto. */
export const SEASON_CARRY = 0.5;

export type TierId = "bronce" | "plata" | "oro" | "platino" | "diamante" | "leyenda";

export interface Tier {
  id: TierId;
  /** Puntuacion desde la que se esta en esta division. */
  min: number;
  /** Color de la insignia. */
  color: number;
}

/** De menos a mas. */
export const TIERS: readonly Tier[] = [
  { id: "bronce", min: 0, color: 0xc07a45 },
  { id: "plata", min: 900, color: 0xc8d0da },
  { id: "oro", min: 1100, color: 0xffd35a },
  { id: "platino", min: 1300, color: 0x6fe0d2 },
  { id: "diamante", min: 1500, color: 0x8fb4ff },
  { id: "leyenda", min: 1700, color: 0xff7ad9 },
];

export interface TierStanding {
  /** `null` mientras juega la colocacion. */
  tier: TierId | null;
  /** Partidos de colocacion que faltan. */
  placementLeft: number;
  /** 0-1: cuanto lleva de su division hacia la siguiente (1 en Leyenda). */
  progress: number;
  /** Puntos que faltan para la siguiente division (0 en Leyenda). */
  toNext: number;
}

export function tierFor(mmr: number, games: number): TierStanding {
  const placementLeft = Math.max(0, PLACEMENT_GAMES - games);
  if (placementLeft > 0) return { tier: null, placementLeft, progress: 0, toNext: 0 };
  let index = 0;
  for (let i = 0; i < TIERS.length; i++) if (mmr >= TIERS[i]!.min) index = i;
  const current = TIERS[index]!;
  const next = TIERS[index + 1];
  if (!next) return { tier: current.id, placementLeft: 0, progress: 1, toNext: 0 };
  const span = next.min - current.min;
  return {
    tier: current.id,
    placementLeft: 0,
    progress: Math.max(0, Math.min(1, (mmr - current.min) / span)),
    toNext: Math.max(0, Math.ceil(next.min - mmr)),
  };
}

/** Probabilidad de ganar de un equipo con media `mine` contra otro con media `theirs`. */
export function expectedScore(mine: number, theirs: number): number {
  return 1 / (1 + 10 ** ((theirs - mine) / 400));
}

/** Cuanto cambia la puntuacion de alguien con `games` partidos por un resultado. */
export function ratingDelta(
  games: number,
  teamAverage: number,
  rivalAverage: number,
  result: "win" | "draw" | "loss",
): number {
  const k = games < PROVISIONAL_GAMES ? K_PROVISIONAL : K_SETTLED;
  const actual = result === "win" ? 1 : result === "draw" ? 0.5 : 0;
  return k * (actual - expectedScore(teamAverage, rivalAverage));
}

/** La puntuacion con la que se empieza una temporada nueva. */
export function seasonCarry(mmr: number): number {
  return RATING_START + (mmr - RATING_START) * SEASON_CARRY;
}

/** Temporada de una fecha: el mes, en UTC ("2026-09"). */
export function seasonKey(at: number): string {
  const d = new Date(at);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** Cuando empieza la temporada de `at` (ms, UTC). */
export function seasonStart(at: number): number {
  const d = new Date(at);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
}

/** Cuando empieza la temporada siguiente. */
export function seasonEnd(at: number): number {
  const d = new Date(at);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
}

/** Cuando empezo la semana de `at`: el lunes a las 00:00 UTC. */
export function weekStart(at: number): number {
  const d = new Date(at);
  const day = (d.getUTCDay() + 6) % 7; // lunes = 0
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day);
}
